<template>
  <div class="refund-operation page--refund-operation">
    <div class="page__header">
      <div class="page__title">
        <router-link :to="backLinkRoute" class="page__back-link">
          <arrow-button
            class="page__back-link-icon"
            :rotated180="true"
          ></arrow-button>
        </router-link>
        <div class="page__title-text">
          {{ $t('refund-operation.page.title') }}
        </div>
      </div>
    </div>
    <div class="refund-operation__content">
      <div class="refund-operation__form">
        <div class="refund-operation__form-fields">
          <div class="refund-operation__form-field">
            <div class="form-field--row">
              <text-input
                v-model:value="refundData.sum"
                :mask="$options.NUMBER_MASK"
                :placeholder="$t('refund-operation.fields.sum-placeholder')"
                :title="$t('refund-operation.fields.sum')"
                @focus="activeInput = 'uah'"
                @blur="activeInput = null"
              ></text-input>

              <text-input
                v-if="showUsdField"
                v-model:value="refundData.usdSum"
                :mask="$options.NUMBER_MASK"
                :placeholder="$t('refund-operation.fields.sum-placeholder')"
                :title="$t('refund-operation.fields.sumUsd')"
                @focus="activeInput = 'usd'"
                @blur="activeInput = null"
              ></text-input>

              <text-input
                v-if="showEurField"
                v-model:value="refundData.eurSum"
                :mask="$options.NUMBER_MASK"
                :placeholder="$t('refund-operation.fields.sum-placeholder')"
                :title="$t('refund-operation.fields.sumEur')"
                @focus="activeInput = 'eur'"
                @blur="activeInput = null"
              ></text-input>
            </div>

            <div class="refund-operation__form-field-hint">
              {{ $t('refund-operation.fields.sum-hint') }}
            </div>
          </div>
          <div class="refund-operation__form-field">
            <text-input
              v-model:value="refundData.comment"
              :title="$t('refund-operation.fields.comment')"
              :placeholder="$t('refund-operation.fields.comment-placeholder')"
            ></text-input>
          </div>
          <div class="refund-operation__form-field">
            <text-input
              v-model:value="refundData.pan"
              :disabled="true"
              :mask="'XXXX XXXX XXXX XXXX'"
              :title="$t('refund-operation.fields.pan')"
              :placeholder="$t('refund-operation.fields.pan-placeholder')"
            ></text-input>
          </div>
        </div>
        <div class="refund-operation__form-actions">
          <primary-button
            class="refund-operation__form-action"
            :disabled="!isRefundButtonActive"
            :label="$t('refund-operation.actions.refund')"
            @click="refundBill"
          ></primary-button>
        </div>
      </div>
      <div
        class="refund-operation__history"
        v-if="transaction.refunds.length > 0"
      >
        <div class="refund-operation__history-title">
          {{ $t('refund-operation.history.title') }}
        </div>
        <div class="refund-operation__history-table">
          <company-bill-refunds-table
            :refunds="transaction.refunds"
          ></company-bill-refunds-table>
        </div>
      </div>
    </div>
    <success-notification
      ref="transactionRefundSuccessNotification"
      :text="$t('refund-operation.notifications.success')"
    ></success-notification>
  </div>
</template>

<script>
import ArrowButton from '@vue-app/components/base/button/ArrowButton.vue'
import TextInput from '@vue-app/components/base/input/TextInput.vue'
import PrimaryButton from '@vue-app/components/base/button/PrimaryButton.vue'
import CustomSelect from '@vue-app/components/base/input/CustomSelect.vue'
import createNumberMask from 'text-mask-addons/dist/createNumberMask'
import moment from 'moment'
import { RouteHelper } from '@js-app/services/helpers/routes/routes-helper'
import CompanyBillRefundsTable from '@vue-app/components/company/bills/table/refund/CompanyBillRefundsTable.vue'
import { CompaniesApi } from '@js-app/office/api/companies-api'
import { Bill } from '@js-app/entities/Bill'
import SuccessNotification from '@vue-app/components/base/popup/notification/SuccessNotification.vue'
import { Transaction } from '@js-app/entities/transaction/Transaction'

export default {
  name: 'RefundOperation',
  props: ['id', 'operationId'],
  components: {
    SuccessNotification,
    CompanyBillRefundsTable,
    CustomSelect,
    PrimaryButton,
    TextInput,
    ArrowButton,
  },
  data() {
    return {
      refundData: {
        currency: 'uah',
        sum: '',
        usdSum: '',
        eurSum: '',
        comment: '',
        pan: '',
      },
      exchangeRate: '',
      transaction: new Transaction(),
      transactionRefunding: false,
      transactionLoading: true,
      backLinkRoute: '',
      activeInput: null,
      maxRefundAmount: 0,
    }
  },

  NUMBER_MASK: createNumberMask({
    prefix: '',
    allowDecimal: true,
    includeThousandsSeparator: false,
    allowNegative: false,
    decimalLimit: 2,
  }),

  created() {
    this.loadTransaction()
  },

  computed: {
    isRefundButtonActive() {
      const refundSum = parseFloat(this.refundData.sum)
      const maxSum = parseFloat(this.maxRefundAmount.toFixed(2))
      return (
        !this.transactionRefunding &&
        refundSum > 0 &&
        refundSum <= maxSum &&
        !this.transactionLoading &&
        (this.transaction.canMakeRefund || this.transaction.canCancelPreAuth)
      )
    },

    showUsdField() {
      return this.exchangeRate !== '1' && this.refundData.currency === 'USD'
    },
    showEurField() {
      return this.exchangeRate !== '1' && this.refundData.currency === 'EUR'
    },
  },

  methods: {
    async loadTransaction() {
      this.transactionLoading = true

      try {
        const currencyRateResponse = await CompaniesApi.getRefundCurrencyRate(
          this.id,
          this.operationId
        )
        this.exchangeRate = currencyRateResponse.data.CurrencyRate

        const transactionResponse = await CompaniesApi.getOperationDetails(
          this.id,
          this.operationId
        )

        this.transaction = new Transaction(
          transactionResponse.data.ReportOperations[0]
        )

        const amount = this.transaction.transactionAmountFloat
        const currency = this.transaction.transactionCurrencyPlainValue

        this.refundData.currency = currency
        this.refundData.pan = this.transaction.cardNumber
        this.maxRefundAmount = this.transaction.transactionAmountFloat

        this.refundData.usdSum = ''
        this.refundData.eurSum = ''
        this.refundData.sum = ''

        if (currency === 'USD' && this.exchangeRate !== '1') {
          this.refundData.usdSum = amount
          this.refundData.sum = (amount * this.exchangeRate).toFixed(2)
          this.maxRefundAmount = amount * this.exchangeRate
        } else if (currency === 'EUR' && this.exchangeRate !== '1') {
          this.refundData.eurSum = amount
          this.refundData.sum = (amount * this.exchangeRate).toFixed(2)
          this.maxRefundAmount = amount * this.exchangeRate
        } else {
          this.refundData.sum = amount
        }
      } catch (error) {
        console.error('Failed to load transaction data:', error)
      } finally {
        this.transactionLoading = false
      }
    },

    disabledDateHandler(val1) {
      return moment().diff(moment(val1), 'days') > 0
    },
    async refundBill() {
      if (this.transactionRefunding) {
        return false
      }

      this.transactionRefunding = true

      let _transactionRefundResponse = null

      try {
        _transactionRefundResponse = await CompaniesApi.makeOperationRefund(
          this.id,
          this.transaction.operationId,
          this.refundData.sum,
          this.transaction.terminalId
        )

        this.$refs.transactionRefundSuccessNotification.show().then(() => {})
        await this.loadTransaction()
      } catch (e) {
        //TODO: Show error toast with error info
        return
      } finally {
        this.transactionRefunding = false
      }
    },
  },

  watch: {
    'refundData.sum'(newVal) {
      if (this.activeInput === 'usd' || this.activeInput === 'eur') return
      const parsed = parseFloat(newVal)
      if (!isNaN(parsed) && this.exchangeRate) {
        this.activeInput = 'uah'

        if (this.showUsdField) {
          this.refundData.usdSum = (parsed / this.exchangeRate).toFixed(2)
        }

        if (this.showEurField) {
          this.refundData.eurSum = (parsed / this.exchangeRate).toFixed(2)
        }
      } else {
        this.refundData.usdSum = ''
        this.refundData.eurSum = ''
      }
    },

    'refundData.usdSum'(newVal) {
      if (this.activeInput === 'uah' || !this.showUsdField) return
      const parsed = parseFloat(newVal)
      if (!isNaN(parsed) && this.exchangeRate) {
        this.activeInput = 'usd'
        this.refundData.sum = (parsed * this.exchangeRate).toFixed(2)
      } else {
        this.refundData.sum = ''
      }
    },

    'refundData.eurSum'(newVal) {
      if (this.activeInput === 'uah' || !this.showEurField) return
      const parsed = parseFloat(newVal)
      if (!isNaN(parsed) && this.exchangeRate) {
        this.activeInput = 'eur'
        this.refundData.sum = (parsed * this.exchangeRate).toFixed(2)
      } else {
        this.refundData.sum = ''
      }
    },
  },

  beforeRouteEnter(to, from, next) {
    next((vm) => {
      vm.backLinkRoute =
        from.path !== '/'
          ? from.path
          : RouteHelper.getRoute('analytics', {
              companyId: to.params.id,
            })
    })
  },
}
</script>

<style lang="scss" scoped>
.page {
  &--refund-operation {
    padding-top: 28px;
  }

  &__header {
    margin-bottom: 24px;
  }
}
.refund-operation {
  &__content {
    display: grid;
    grid-template-columns: 390px 1fr;
    column-gap: 15px;
  }

  .form-field--row {
    display: flex;
    gap: 10px;
  }

  &__form {
    background: #ffffff;
    border-radius: 22px;
    padding: 60px 50px 74px;
    width: 100%;
    max-width: 390px;

    &-fields {
      margin-bottom: 40px;
    }

    &-field {
      margin-bottom: 25px;

      &-hint {
        width: 100%;
        font-weight: 500;
        font-size: 10px;
        line-height: 14px;
        color: #b9c4d8;
        margin-top: 5px;
        flex-wrap: wrap;
      }

      &--row {
        display: flex;
        align-items: stretch;
        flex-wrap: wrap;
      }

      &-small {
        width: 95px;
        margin-right: 26px;
        flex-shrink: 0;
        flex-grow: 0;
      }

      &-big {
        width: calc(100% - 121px);
        flex-shrink: 1;
        flex-grow: 1;
      }

      &:last-child {
        margin-bottom: 0;
      }
    }

    &-action {
      padding: 25px 25px;
    }
  }

  &__history {
    &-title {
      background: #ffffff;
      border-radius: 8px;
      padding: 7px 28px 8px;
      font-weight: 600;
      font-size: 16px;
      line-height: 28px;
      color: #465a8b;
    }

    &-table {
      margin-top: 15px;
    }
  }
}
</style>
